import { motion } from "framer-motion";
import { ShieldCheck } from "lucide-react";

const STEPS = [
    {
        number: "01",
        title: "Select Service & Location",
        description:
            "Enter your vehicle, pick the symptoms or service, and drop your address — home, office or roadside.",
    },
    {
        number: "02",
        title: "Master Mechanic Dispatched",
        description:
            "Our mobile unit arrives with OEM tools, diagnostic computers and premium replacement components.",
    },
    {
        number: "03",
        title: "Transparent Estimate First",
        description:
            "You approve a fixed quote before any wrench turns. Watch the repair or continue your day uninterrupted.",
    },
    {
        number: "04",
        title: "Digital Sign-Off & Warranty",
        description:
            "Test-drive validation, contactless invoicing and a 24-Month / 24,000-Mile nationwide warranty.",
    },
];

export default function HowItWorks() {
    return (
        <section id="how-it-works" data-testid="how-it-works-section" className="py-24 sm:py-32 bg-[#0B0C0E] border-y border-[#22262F]">
            <div className="max-w-7xl mx-auto px-6">
                <motion.div
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="mb-16 max-w-2xl"
                >
                    <div className="text-xs font-mono uppercase tracking-[0.25em] text-amber-400 font-semibold mb-4">
                        02 / Protocol
                    </div>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white leading-tight">
                        Four steps. Zero hassle.
                    </h2>
                </motion.div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {STEPS.map((step, i) => (
                        <motion.div
                            key={step.number}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: i * 0.1 }}
                            data-testid={`how-it-works-step-${step.number}`}
                            className="relative rounded-2xl border border-[#22262F] bg-[#121418] p-8 hover:border-amber-500/40 transition-colors duration-300"
                        >
                            <div className="font-display text-6xl font-extrabold text-outline leading-none">
                                {step.number}
                            </div>
                            <h3 className="mt-6 font-display text-xl font-bold uppercase tracking-wide text-zinc-100">
                                {step.title}
                            </h3>
                            <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{step.description}</p>
                        </motion.div>
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    className="mt-10 flex items-center gap-3 text-zinc-400"
                    data-testid="warranty-badge"
                >
                    <ShieldCheck className="w-5 h-5 text-amber-500" />
                    <span className="text-xs font-mono uppercase tracking-[0.2em]">
                        Every repair backed by our 24-Month / 30,000-KM warranty
                    </span>
                </motion.div>
            </div>
        </section>
    );
}
